import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";

export const ATM_FAQS = [
    {
        q: "Do you work only with international clients?",
        a: "No.\n\nWe support international clients moving to Toronto, clients relocating from another Canadian city, and local Toronto/GTA moves.",
    },
    {
        q: "Do you provide international moving or freight shipping?",
        a: "No. AllTask Moves does not provide international freight, customs, or shipping services. We focus on Toronto-side relocation coordination and local move support.",
    },
    {
        q: "Do you guarantee that I will get a rental home?",
        a: "No. We do not guarantee housing, landlord approval, or rental acceptance.\n\nWe can help with consultation, shortlisting, viewing support, and communication coordination, but the final decision is between the client, landlord, property manager, or listing representative.",
    },
    {
        q: "Do you provide legal or immigration advice?",
        a: "No. AllTask Moves does not provide legal, immigration, financial, or regulated professional advice.\n\nFor those matters, clients should speak with qualified licensed professionals.",
    },
    {
        q: "Can you attend a viewing for me?",
        a: "Yes, where available.\n\nOur viewing-on-behalf service includes up to 4 hours and up to 3 viewings within the GTA +/-10 km. We provide practical observations, photos or videos where permitted, and communication support.\n\nWe do not provide professional property inspection.",
    },
    {
        q: "What happens if the work takes more time than expected?",
        a: "Additional time is billed only with client approval.\n\nWe confirm before extending beyond the agreed time block, so there are no silent time extensions or surprise charges.",
    },
    {
        q: "Do you provide movers?",
        a: "We help coordinate moving support through available task partners where appropriate.\n\nThe exact arrangement depends on location, timing, scope, client needs, and task partner availability.",
    },
    {
        q: "Do you offer packing and cleaning?",
        a: "Yes. We can help coordinate packing and cleaning support based on your move needs, property size, timing, and task partner availability.",
    },
    {
        q: "Who are AllTask Moves task partners?",
        a: "Task partners are independent service providers who may support moving, packing, cleaning, loading, organizing, furniture assembly, disposal, or other move-related tasks.\n\nWe work to match the right task partner to the right job based on the client's needs, service scope, timing, location, and availability.",
    },
    {
        q: "How do you enlist task partners?",
        a: "AllTask Moves reviews task partners before assigning them to client work.\n\nDepending on the service type, this may include identity verification, contact details, service experience, references, insurance information, background-check requirements, availability, service area, and pricing expectations.\n\nFor client-facing or in-home tasks, we may apply additional screening requirements.",
    },
    {
        q: "Are task partners AllTask employees?",
        a: "Task partners may be independent contractors, vendors, or service providers, depending on the arrangement.\n\nAllTask Moves coordinates the service process and works with task partners suited to the client's needs, but the exact structure may vary by task, service type, and scope.",
    },
    {
        q: "How transparent is the work process?",
        a: "Transparency is central to how AllTask Moves works.\n\nBefore paid work begins, we confirm the service, price, time block, scope, deliverables, exclusions, and payment requirements.\n\nDuring active coordination, we aim to keep clients updated on relevant timing, vendor arrangements, changes, and next steps.",
    },
    {
        q: "Will I know what I am paying for?",
        a: "Yes.\n\nWe confirm the service scope and pricing before work begins. Additional time or added services require client approval.\n\nOur goal is simple: clear scope first, work second, no hidden extensions.",
    },
    {
        q: "Is AllTask Moves insured?",
        a: "Yes. AllTask Moves is fully insured for its business operations.\n\nOur insurance coverage is intended to support responsible operations, client trust, and service delivery. Some specialized tasks may also require task partners to carry their own insurance, depending on the nature of the work.",
    },
    {
        q: "Are task partners insured?",
        a: "Where the task requires it, AllTask Moves may request insurance information from task partners.\n\nFor example, movers, cleaners, packers, drivers, or other service providers may need to provide relevant insurance or compliance information before being assigned to certain client work.",
    },
    {
        q: "How do payments work?",
        a: "Free consultations require no payment.\n\nPaid services such as consultation, shortlisting, viewing support, and move-day coordination may require prepayment or deposit through our website. Quote-based services are confirmed before payment.",
    },
    {
        q: "How do I start?",
        a: "Start by booking a free 20-minute consultation.\n\nWe will review your move, understand your needs, and recommend the next best step.",
    },
];

export default function Faq({
    items = ATM_FAQS,
    eyebrow = "FAQ",
    title = "A few quick answers.",
    intro = "Quick clarity on what AllTask Moves does, how we work, and what clients can expect.",
    id = "faq",
}) {
    const [open, setOpen] = useState(null);

    return (
        <section
            id={id}
            className="section relative overflow-hidden"
            data-testid="faq-section"
        >
            <div className="container-atm">
                <div className="grid gap-10 md:grid-cols-12 md:items-start">
                    <div className="md:col-span-5">
                        <span className="eyebrow">{eyebrow}</span>
                        <h2 className="mt-4 font-display text-3xl font-semibold leading-[1.1] text-[#0E2A47] sm:text-4xl lg:text-5xl">
                            {title}
                        </h2>
                        <p className="mt-4 max-w-md text-[15px] text-[#475569]">
                            {intro}
                        </p>
                    </div>

                    <div className="md:col-span-7">
                        <ul className="space-y-3" data-testid="faq-list">
                            {items.map((f, i) => {
                                const isOpen = open === i;
                                return (
                                    <li key={f.q} data-testid={`faq-item-${i}`}>
                                        <button
                                            type="button"
                                            onClick={() => setOpen(isOpen ? null : i)}
                                            aria-expanded={isOpen}
                                            data-testid={`faq-toggle-${i}`}
                                            className={`w-full rounded-2xl border bg-white px-5 py-4 text-left transition-all duration-300 ${
                                                isOpen
                                                    ? "border-[#F26B1F] shadow-[0_18px_36px_-22px_rgba(242,107,31,0.55)]"
                                                    : "border-[#ECE4D3] hover:border-[#0E2A47]/30"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="font-display text-base font-semibold text-[#0E2A47] md:text-lg">
                                                    {f.q}
                                                </span>
                                                <motion.span
                                                    animate={{ rotate: isOpen ? 45 : 0 }}
                                                    transition={{ duration: 0.3 }}
                                                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                                                        isOpen
                                                            ? "bg-[#F26B1F] text-white"
                                                            : "bg-[#FAF6EE] text-[#F26B1F]"
                                                    }`}
                                                >
                                                    <Plus size={16} strokeWidth={2.4} />
                                                </motion.span>
                                            </div>
                                            <AnimatePresence initial={false}>
                                                {isOpen && (
                                                    <motion.div
                                                        key="body"
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{
                                                            height: "auto",
                                                            opacity: 1,
                                                        }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{
                                                            duration: 0.32,
                                                            ease: [0.16, 1, 0.3, 1],
                                                        }}
                                                        className="overflow-hidden"
                                                    >
                                                        <p className="mt-3 whitespace-pre-line text-[14px] leading-relaxed text-[#475569] md:text-[15px]">
                                                            {f.a}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}
